import { zodResolver } from '@hookform/resolvers/zod';
import { Ionicons } from '@expo/vector-icons';
import { useQueryClient } from '@tanstack/react-query';
import { format, isValid, parse } from 'date-fns';
import { router } from 'expo-router';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { ActivityIndicator, View } from 'react-native';
import { z } from 'zod';

import { ControlledInput } from '@/components/forms/controlled-input';
import { ControlledSearchableSelect } from '@/components/forms/controlled-searchable-select';
import { ControlledDataTableSelect } from '@/components/forms/controlled-datatable-select';
import { ControlledSelect } from '@/components/forms/controlled-select';
import { CollapseSection } from '@/components/enrollments/collapse-section';
import {
  ControlledOptionRows,
  ControlledStepperRow,
} from '@/components/services/wizard/field';
import { ScheduleGrade } from '@/components/schedule/schedule-grade';
import {
  WEEKDAYS_SHORT,
  slotKey,
  useScheduleGrade,
} from '@/components/schedule/use-schedule-grade';
import { ThemedText } from '@/components/themed-text';
import { LoadingButton } from '@/components/ui/loading-button';
import { PageLayout } from '@/components/ui/page-layout';
import { PressableScale } from '@/components/ui/pressable-scale';
import { StepIndicator } from '@/components/ui/step-indicator';
import { appointmentKeys, useProviderAvailability } from '@/hooks/use-appointments';
import { useProviderProfile } from '@/hooks/use-provider';
import { useProviderPreviewRecurrence } from '@/hooks/use-recurrences';
import {
  formatSlotTime,
  zonedDateKey,
  zonedLocalToIso,
  zonedWeekday,
} from '@/utils/format-appointment';
import api from '@/services/api';
import { useToastStore } from '@/store/toastStore';
import { showPlanLimit } from '@/store/planLimitStore';
import { BILLING_MODEL } from '@/types/billing-model';
import type { Client } from '@/types/client';
import { ENROLLMENT_STATUS } from '@/types/enrollment-status';
import type { CreateEnrollmentDto, Enrollments } from '@/types/enrollments';
import { PROCESSING_METHOD } from '@/types/processing-method';
import { QUOTA_PERIOD } from '@/types/quota-period';
import { RECURRENCE_INTERVAL } from '@/types/recurrence-interval';
import { REMINDER_TYPE } from '@/types/reminder-type';
import { useFormFooterInset } from '@/components/mobile-bottom-nav';
import type { Service } from '@/types/service';
import { formatCurrency } from '@/utils/format-currency';
import { parseCurrency } from '@/utils/parse-currency';
import { getApiErrorMessage } from '@/utils/api-error';
import { ErrorState } from '@/components/ui/error-state';
import { usePlanFeature } from '@/hooks/use-plan-feature';
import { navigateToSubscriptionPlans } from '@/lib/auth/subscription-plans-url';

type ContractFormScreenProps = {
  /** Serviço vindo da rota (criação a partir da tela do serviço). */
  serviceIdParam?: string;
  /** Presente = edição de um contrato existente. */
  enrollmentId?: string;
};

const DATE_RE = /^\d{2}\/\d{2}\/\d{4}$/;

const contractSchema = z.object({
  serviceId: z.string().min(1, 'Escolha o serviço.'),
  clientId: z.string().min(1, 'Escolha o cliente.'),
  /** Vazios = herda do serviço. */
  priceOverride: z.string().optional(),
  quotaOverride: z.number().int().positive().nullable().optional(),
  startDate: z.string().regex(DATE_RE, 'Data de início inválida.'),
  endDate: z
    .string()
    .optional()
    .refine((v) => !v || DATE_RE.test(v), 'Data de término inválida.'),
  chargeDay: z
    .string()
    .min(1, 'Informe o dia da cobrança.')
    .refine((v) => {
      const n = Number(v);
      return Number.isInteger(n) && n >= 1 && n <= 31;
    }, 'Use um dia entre 1 e 31.'),
  reminderType: z.nativeEnum(REMINDER_TYPE),
  status: z.nativeEnum(ENROLLMENT_STATUS).optional(),
  pixCopyPaste: z.string().optional(),
  paymentLink: z.string().optional(),
  bankName: z.string().optional(),
  bankAgency: z.string().optional(),
  bankAccount: z.string().optional(),
  paymentInstructions: z.string().optional(),
});

type ContractForm = z.infer<typeof contractSchema>;

/**
 * Dia 'yyyy-MM-dd' n ciclos de cota depois do início: n meses (clampado no fim
 * do mês, mesma regra do `periodBounds` do backend) ou n semanas.
 */
function shiftCycle(startKey: string, n: number, period: QUOTA_PERIOD): string {
  const [y, m, d] = startKey.split('-').map(Number);
  if (period === QUOTA_PERIOD.WEEK) return toIsoDate(new Date(y, m - 1, d + 7 * n));
  const lastDay = new Date(y, m + n, 0).getDate();
  return toIsoDate(new Date(y, m - 1 + n, Math.min(d, lastDay)));
}

/** Ciclo de cota em curso — ou o primeiro, se o contrato ainda não começou: [start, end). */
function currentCycle(startKey: string, todayKey: string, period: QUOTA_PERIOD) {
  let n = 0;
  while (shiftCycle(startKey, n + 1, period) <= todayKey) n += 1;
  return { start: shiftCycle(startKey, n, period), end: shiftCycle(startKey, n + 1, period) };
}

const STEPS = [
  { title: 'Assinante', icon: 'person-outline' as const },
  { title: 'Cobrança', icon: 'cash-outline' as const },
];

const REMINDER_OPTIONS = [
  { value: REMINDER_TYPE.ONLY_ON_DUE_DATE, label: 'Só no dia do vencimento' },
  {
    value: REMINDER_TYPE.THREE_DAYS_BEFORE_AND_ON_DUE,
    label: '3 dias antes + no dia',
  },
  {
    value: REMINDER_TYPE.FIVE_DAYS_BEFORE_AND_ON_DUE,
    label: '5 dias antes + no dia',
  },
];

const STATUS_OPTIONS = [
  { label: 'Ativo', value: ENROLLMENT_STATUS.ACTIVE },
  { label: 'Pausado', value: ENROLLMENT_STATUS.INACTIVE },
  { label: 'Concluído', value: ENROLLMENT_STATUS.COMPLETED },
  { label: 'Cancelado', value: ENROLLMENT_STATUS.CANCELLED },
];

const parseFormDate = (s?: string): Date | null => {
  if (!s || !DATE_RE.test(s)) return null;
  const d = parse(s, 'dd/MM/yyyy', new Date());
  return isValid(d) ? d : null;
};

const toIsoDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(
    2,
    '0',
  )}`;

/** Data "pura" do banco (yyyy-MM-dd ou Date) → dd/MM/yyyy para o campo. */
const dbDateToField = (d: Date | string | undefined): string | undefined => {
  if (!d) return undefined;
  const parsed = typeof d === 'string' ? parse(d.slice(0, 10), 'yyyy-MM-dd', new Date()) : d;
  return isValid(parsed) ? format(parsed, 'dd/MM/yyyy') : undefined;
};

/** Segmento retangular: usar o serviço como está × ajustar para este cliente. */
function ModeSwitch({
  value,
  onChange,
}: {
  value: 'keep' | 'adjust';
  onChange: (v: 'keep' | 'adjust') => void;
}) {
  return (
    <View className="mb-3 flex-row gap-1.5 rounded-2xl bg-neutral-100 p-1">
      {(
        [
          ['keep', 'Usar como está'],
          ['adjust', 'Ajustar p/ este cliente'],
        ] as const
      ).map(([v, label]) => {
        const on = value === v;
        return (
          <PressableScale
            key={v}
            haptic
            onPress={() => onChange(v)}
            accessibilityRole="tab"
            accessibilityState={{ selected: on }}
            className={`h-11 flex-1 items-center justify-center rounded-xl ${
              on ? 'bg-card shadow-sm' : ''
            }`}
            style={{ borderCurve: 'continuous' }}
          >
            <ThemedText
              className={`font-semibold text-[13px] ${
                on ? 'text-neutral-900' : 'text-neutral-500'
              }`}
            >
              {label}
            </ThemedText>
          </PressableScale>
        );
      })}
    </View>
  );
}

function StepCard({ children }: { children: React.ReactNode }) {
  return (
    <View
      className="gap-4 rounded-2xl border border-neutral-900/5 bg-card p-4"
      style={{ borderCurve: 'continuous' }}
    >
      {children}
    </View>
  );
}

export function ContractFormScreen({ serviceIdParam, enrollmentId }: ContractFormScreenProps) {
  const isEdit = !!enrollmentId;
  const { addToast } = useToastStore();
  const bottomInset = useFormFooterInset();
  const queryClient = useQueryClient();
  const { data: provider } = useProviderProfile();
  const subscriptionPlan = usePlanFeature('SUBSCRIPTIONS');

  const [step, setStep] = useState(1);
  const [mode, setMode] = useState<'keep' | 'adjust'>('keep');
  const [service, setService] = useState<Service | null>(null);
  const [serviceName, setServiceName] = useState<string | undefined>();
  const [enrollment, setEnrollment] = useState<Enrollments | null>(null);
  const [loadingEnrollment, setLoadingEnrollment] = useState(isEdit);
  const [hadGrade, setHadGrade] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [openGrade, setOpenGrade] = useState(false);
  const [openPayment, setOpenPayment] = useState(false);

  const { control, handleSubmit, setValue, getValues, trigger, reset } = useForm<ContractForm>({
    resolver: zodResolver(contractSchema),
    mode: 'onTouched',
    defaultValues: {
      serviceId: serviceIdParam ?? '',
      clientId: '',
      priceOverride: undefined,
      quotaOverride: null,
      startDate: format(new Date(), 'dd/MM/yyyy'),
      endDate: undefined,
      chargeDay: String(new Date().getDate()),
      reminderType: REMINDER_TYPE.THREE_DAYS_BEFORE_AND_ON_DUE,
      status: undefined,
    },
  });

  const watchedServiceId = useWatch({ control, name: 'serviceId' });
  const chargeDay = useWatch({ control, name: 'chargeDay' });
  const quotaOverride = useWatch({ control, name: 'quotaOverride' });
  const watchedStartDate = useWatch({ control, name: 'startDate' });
  /** Início do contrato em "yyyy-MM-dd" pro preview da grade — ancora horizonte e cota. */
  const gradeStartDate = useMemo(() => {
    const parsed = parseFormDate(watchedStartDate);
    return parsed ? toIsoDate(parsed) : undefined;
  }, [watchedStartDate]);

  const fixedServiceId = serviceIdParam ?? enrollment?.service?.id;

  // Serviço escolhido → carrega o que a assinatura herda (preço, cota).
  useEffect(() => {
    if (!watchedServiceId) {
      setService(null);
      return;
    }
    let alive = true;
    api
      .get<Service>(`service/${watchedServiceId}`)
      .then(({ data }) => {
        if (!alive) return;
        setService(data);
        setServiceName(data.name);
      })
      .catch(() => {
        if (alive) addToast('Não foi possível carregar o serviço.', 'error');
      });
    return () => {
      alive = false;
    };
  }, [watchedServiceId, addToast]);

  // Edição: carrega o contrato e preenche o formulário.
  useEffect(() => {
    if (!isEdit) return;
    let alive = true;
    (async () => {
      try {
        const { data: e } = await api.get<Enrollments>(`enrollments/${enrollmentId}`);
        let svc: Service | null = e.service ?? null;
        if (e.service?.id) {
          try {
            const r = await api.get<Service>(`service/${e.service.id}`);
            svc = r.data;
          } catch {
            /* usa e.service */
          }
        }
        if (!alive) return;

        const sched = e.serviceSchedules?.[0];
        const gradeSet = !!sched?.daysOfWeek?.length;
        const isSub = !!svc?.isSubscription && !!svc?.subscriptionQuota;
        const priceDiffers = svc ? Number(e.price ?? 0) !== Number(svc.defaultPrice ?? 0) : false;
        const quotaDiffers =
          isSub && e.quotaPerPeriod != null && e.quotaPerPeriod !== svc?.subscriptionQuota;
        const initialMode: 'keep' | 'adjust' = priceDiffers || quotaDiffers ? 'adjust' : 'keep';
        const anyE = e as unknown as Record<string, string | undefined>;

        setEnrollment(e);
        setService(svc);
        setServiceName(svc?.name ?? e.service?.name);
        setMode(initialMode);
        setHadGrade(gradeSet);
        setOpenGrade(gradeSet);

        reset({
          serviceId: e.service?.id ?? '',
          clientId: e.client?.id ?? '',
          priceOverride: initialMode === 'adjust' ? formatCurrency(e.price) : undefined,
          quotaOverride: initialMode === 'adjust' ? (e.quotaPerPeriod ?? null) : null,
          startDate: dbDateToField(e.startDate) ?? format(new Date(), 'dd/MM/yyyy'),
          endDate: dbDateToField(e.endDate),
          chargeDay:
            e.chargeSchedule?.chargeDay != null
              ? String(e.chargeSchedule.chargeDay)
              : String(new Date().getDate()),
          reminderType:
            e.chargeSchedule?.reminderType ?? REMINDER_TYPE.THREE_DAYS_BEFORE_AND_ON_DUE,
          status: e.status,
          pixCopyPaste: e.pixCopyPaste ?? '',
          paymentLink: e.paymentLink ?? '',
          bankName: anyE.bankName ?? '',
          bankAgency: anyE.bankAgency ?? '',
          bankAccount: anyE.bankAccount ?? '',
          paymentInstructions: anyE.paymentInstructions ?? '',
        });
        setLoadingEnrollment(false);
      } catch {
        if (alive) {
          addToast('Não foi possível carregar o contrato.', 'error');
          setLoadingEnrollment(false);
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [isEdit, enrollmentId, reset, addToast]);

  // Criação: dados de pagamento nascem do padrão da conta.
  useEffect(() => {
    if (isEdit || !provider) return;
    // Em equipe "caixa único", quem recebe é o dono: o perfil vem pronto.
    const pix = provider.paymentProfile?.pixCopyPaste ?? provider.defaultPixCopyPaste;
    const link = provider.paymentProfile?.paymentLink ?? provider.defaultPaymentLink;
    if (pix) setValue('pixCopyPaste', pix);
    if (link) setValue('paymentLink', link);
    const bank = provider.paymentProfile?.bankDetails ?? provider.defaultBankDetails;
    if (bank?.bankName) setValue('bankName', bank.bankName);
    if (bank?.bankAgency) setValue('bankAgency', bank.bankAgency);
    if (bank?.bankAccount) setValue('bankAccount', bank.bankAccount);
    if (bank?.paymentInstructions) setValue('paymentInstructions', bank.paymentInstructions);
  }, [isEdit, provider, setValue]);

  const isSubscription = !!service?.isSubscription && !!service?.subscriptionQuota;
  const quotaPeriod = service?.subscriptionQuotaPeriod ?? QUOTA_PERIOD.MONTH;
  const periodWord = quotaPeriod === QUOTA_PERIOD.WEEK ? 'semana' : 'mês';

  // `defaultPrice` é decimal no banco → chega como string no JSON. Coage aqui
  // para não vazar string no payload (o DTO exige number).
  const servicePrice = Number(service?.defaultPrice ?? 0) || 0;
  const serviceQuota = service?.subscriptionQuota ?? undefined;
  /** Cota que vale para este contrato: ajuste do prestador, senão a do serviço. */
  const effectiveQuota = mode === 'adjust' && quotaOverride != null ? quotaOverride : serviceQuota;
  const chargeDayNote = useMemo(() => {
    const n = Number(chargeDay);
    if (n >= 29 && n <= 31) return 'meses curtos: cobra no último dia';
    return '1 a 31';
  }, [chargeDay]);

  // Disponibilidade real do serviço para pré-marcar vendo o que já está ocupado,
  // só dentro do ciclo de cota do contrato (um mês — ou semana — a partir do
  // início): é o trecho que a cota cobre e que o backend pré-marca.
  // Só busca quando a seção está aberta e é assinatura nova.
  const gradeRange = useMemo(() => {
    const now = new Date();
    const todayKey = zonedDateKey(now.toISOString());
    const cycle = currentCycle(gradeStartDate ?? todayKey, todayKey, quotaPeriod);
    return {
      from: cycle.start > todayKey ? zonedLocalToIso(cycle.start, '00:00') : now.toISOString(),
      to: zonedLocalToIso(cycle.end, '00:00'),
    };
  }, [gradeStartDate, quotaPeriod]);
  const gradeAvailability = useProviderAvailability({
    serviceId: watchedServiceId || undefined,
    from: gradeRange.from,
    to: gradeRange.to,
    enabled: isSubscription && openGrade && !hadGrade,
  });
  const gradePreview = useProviderPreviewRecurrence();
  /**
   * A agenda é a mesma do cliente — mesma mecânica de repetir, liberar uma
   * data e trocar por outra. Aqui ela só nasce com os termos do contrato.
   */
  const grade = useScheduleGrade({
    serviceId: watchedServiceId,
    preview: gradePreview.mutateAsync,
    canRepeat: isSubscription,
    isSubscription,
    quota: effectiveQuota ?? null,
    quotaPeriod,
    startDate: gradeStartDate,
    onWarn: (message) => addToast(message, 'warning'),
  });

  const handleModeChange = (next: 'keep' | 'adjust') => {
    setMode(next);
    if (next === 'adjust') {
      if (service) {
        const current = enrollment?.price ?? servicePrice;
        setValue('priceOverride', formatCurrency(current));
        setValue('quotaOverride', enrollment?.quotaPerPeriod ?? serviceQuota ?? null);
      }
    } else {
      setValue('priceOverride', undefined);
      setValue('quotaOverride', null);
    }
  };

  const goToCharge = async () => {
    const ok = await trigger(['serviceId', 'clientId', 'startDate', 'endDate']);
    if (!ok) {
      addToast('Escolha o serviço, o cliente e a data de início.', 'warning');
      return;
    }
    setStep(2);
  };

  const buildPayload = (form: ContractForm): CreateEnrollmentDto | null => {
    const start = parseFormDate(form.startDate);
    if (!start) return null;
    const end = parseFormDate(form.endDate);

    const priceOverride = form.priceOverride?.trim()
      ? parseCurrency(form.priceOverride)
      : undefined;
    const price = Number(priceOverride ?? servicePrice) || 0;

    const dayNum = Number(form.chargeDay);

    const payload: CreateEnrollmentDto = {
      price,
      startDate: toIsoDate(start) as unknown as Date,
      endDate: end ? (toIsoDate(end) as unknown as Date) : undefined,
      serviceId: form.serviceId,
      clientId: form.clientId,
      processingMethod: PROCESSING_METHOD.MANUAL,
      chargeSchedule: {
        billingModel: BILLING_MODEL.RECURRING,
        recurrenceInterval: RECURRENCE_INTERVAL.MONTHLY,
        chargeDay: Number.isNaN(dayNum) ? 1 : Math.min(31, Math.max(1, dayNum)),
        reminderType: form.reminderType,
      },
      pixCopyPaste: form.pixCopyPaste || undefined,
      paymentLink: form.paymentLink || undefined,
      bankName: form.bankName || undefined,
      bankAgency: form.bankAgency || undefined,
      bankAccount: form.bankAccount || undefined,
      paymentInstructions: form.paymentInstructions || undefined,
    };

    if (isEdit && form.status) payload.status = form.status;

    if (isSubscription) {
      const q = form.quotaOverride != null ? Number(form.quotaOverride) : serviceQuota;
      if (q) {
        payload.quotaPerPeriod = q;
        payload.quotaPeriod = quotaPeriod;
      }
    }

    // "Repetir toda semana": vira grade recorrente (uma linha por dia+horário).
    // Datas soltas NÃO entram aqui — viram agendamentos concretos no onSubmit.
    if (grade.repeat && grade.slots.length > 0) {
      type GradeRow = {
        frequency: 'WEEKLY';
        daysOfWeek: number[];
        startTime: string;
        endTime: string;
        intervalWeeks: number;
        /** Data tocada: a repetição começa nela, não no início do contrato. */
        startsOn: string;
        /** Datas liberadas à mão: a linha pula só elas. */
        skipDates?: string[];
      };
      const byKey = new Map<string, GradeRow>();
      for (const slot of grade.slots) {
        const weekday = zonedWeekday(slot.startAt);
        const startTime = formatSlotTime(slot.startAt);
        const startsOn = zonedDateKey(slot.startAt);
        const key = `${weekday}-${startTime}`;
        const existing = byKey.get(key);
        if (existing) {
          if (startsOn < existing.startsOn) existing.startsOn = startsOn;
          continue;
        }
        byKey.set(key, {
          frequency: 'WEEKLY',
          daysOfWeek: [weekday],
          startTime,
          endTime: formatSlotTime(slot.endAt),
          intervalWeeks: 1,
          startsOn,
        });
      }
      for (const skip of grade.skips) {
        const row = byKey.get(slotKey({ startAt: skip }));
        if (row) row.skipDates = [...(row.skipDates ?? []), zonedDateKey(skip)];
      }
      (payload as unknown as Record<string, unknown>).serviceSchedules = {
        frequency: 'WEEKLY',
        rows: [...byKey.values()],
      };
    }

    return payload;
  };

  const onSubmit = async (form: ContractForm) => {
    if (!service) {
      addToast('Aguarde o serviço carregar.', 'warning');
      return;
    }
    if (grade.overQuota) {
      setOpenGrade(true);
      addToast(
        `A grade fixa passa das ${effectiveQuota} sessões/${periodWord} da assinatura.`,
        'warning',
      );
      return;
    }
    const payload = buildPayload(form);
    if (!payload) {
      addToast('Data de início inválida.', 'error');
      return;
    }
    setSubmitting(true);
    try {
      let targetId: string | undefined = enrollmentId;
      if (isEdit) {
        await api.patch(`enrollments/${enrollmentId}`, payload);
      } else {
        const { data } = await api.post('enrollments', payload);
        targetId = data?.id;
      }

      // Datas soltas (ou, com "repetir", as trocas das datas liberadas) →
      // agendamentos concretos ligados à assinatura recém-criada.
      let preMarkFailed = 0;
      const oneOffSlots = grade.repeat ? grade.extras : grade.slots;
      if (oneOffSlots.length > 0) {
        const results = await Promise.allSettled(
          oneOffSlots.map((slot) =>
            api.post('/provider/me/schedule/appointments', {
              clientId: form.clientId,
              serviceId: form.serviceId,
              startAt: slot.startAt,
            }),
          ),
        );
        preMarkFailed = results.filter((r) => r.status === 'rejected').length;
      }
      // A grade fixa já sai materializada do backend; a agenda precisa recarregar.
      queryClient.invalidateQueries({ queryKey: appointmentKeys.all });

      addToast(
        preMarkFailed > 0
          ? `Contrato ${isEdit ? 'salvo' : 'criado'}, mas ${preMarkFailed} ${
              preMarkFailed === 1 ? 'data não pôde ser marcada' : 'datas não puderam ser marcadas'
            }.`
          : isEdit
            ? 'Contrato salvo.'
            : 'Contrato criado.',
        preMarkFailed > 0 ? 'warning' : 'success',
      );
      router.replace(
        targetId ? `/(tabs)/(provider)/enrollments/${targetId}` : '/(tabs)/(provider)/enrollments',
      );
    } catch (err) {
      if (!showPlanLimit(err)) {
        addToast(
          getApiErrorMessage(
            err,
            isEdit ? 'Não foi possível salvar o contrato.' : 'Não foi possível criar o contrato.',
          ),
          'error',
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const leaveScreen = () => {
    if (isEdit) {
      router.replace(`/(tabs)/(provider)/enrollments/${enrollmentId}`);
    } else if (serviceIdParam) {
      router.replace(`/(tabs)/(provider)/services/${serviceIdParam}` as never);
    } else {
      router.replace('/(tabs)/(provider)/enrollments' as never);
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
      return;
    }
    leaveScreen();
  };

  // Contrato é sempre cobrança recorrente: no gratuito não nasce contrato novo.
  // Editar segue aberto — a API deixa cancelar ou encerrar o que já existe.
  if (!isEdit && subscriptionPlan.locked) {
    return (
      <PageLayout title="Novo contrato" onBack={leaveScreen}>
        <ErrorState
          icon="repeat-outline"
          title="Contratos fazem parte dos planos pagos"
          message="Com um plano, você vende mensalidade e pacote de sessões, com a cobrança gerada todo mês. No gratuito, marque sessões avulsas pela agenda."
          onRetry={() =>
            void navigateToSubscriptionPlans({ push: (path) => router.push(path as never) })
          }
          retryLabel="Ver planos"
        />
      </PageLayout>
    );
  }

  if (isEdit && loadingEnrollment) {
    return (
      <PageLayout title="Editar contrato" onBack={leaveScreen}>
        <ThemedText className="px-1 text-sm text-neutral-500">Carregando contrato…</ThemedText>
      </PageLayout>
    );
  }

  const footer =
    step === 1 ? (
      <View className="px-5 pt-3" style={{ paddingBottom: 8 + bottomInset }}>
        <LoadingButton title="Continuar" onPress={goToCharge} loading={false} />
      </View>
    ) : (
      <View className="flex-row gap-3 px-5 pt-3" style={{ paddingBottom: 8 + bottomInset }}>
        <PressableScale
          haptic
          onPress={() => setStep(1)}
          className="h-12 w-24 items-center justify-center rounded-full border border-neutral-200 bg-card"
        >
          <ThemedText className="font-bold text-sm text-neutral-800">Voltar</ThemedText>
        </PressableScale>
        <View className="flex-1">
          <LoadingButton
            title={isEdit ? 'Salvar' : 'Criar contrato'}
            onPress={handleSubmit(onSubmit)}
            loading={submitting}
          />
        </View>
      </View>
    );

  return (
    <>
      <PageLayout
        title={isEdit ? 'Editar contrato' : 'Novo contrato'}
        subtitle={
          isEdit
            ? 'Altere o que precisar e toque em Salvar.'
            : 'O cliente assina o serviço: paga todo mês e marca dentro da cota.'
        }
        onBack={handleBack}
        backDisabled={submitting}
        footer={footer}
      >
        <StepIndicator steps={STEPS} currentStep={step} className="mb-1" />

        {/* Os dois passos ficam montados: voltar não perde o que já foi escolhido. */}
        <View className="gap-3" style={{ display: step === 1 ? 'flex' : 'none' }}>
          {/* Cartão do serviço — verdade herdada */}
          {fixedServiceId ? (
            <View
              className="rounded-2xl border border-neutral-900/5 bg-card p-4"
              style={{ borderCurve: 'continuous' }}
            >
              <View className="flex-row items-center gap-3">
                <View className="h-9 w-9 items-center justify-center rounded-xl bg-primary/10 dark:bg-primary">
                  <Ionicons className="text-primary dark:text-white" name="briefcase-outline" size={17} />
                </View>
                <ThemedText className="flex-1 font-bold text-[15px] text-neutral-900">
                  {serviceName ?? 'Carregando serviço…'}
                </ThemedText>
                <PressableScale
                  haptic
                  onPress={() =>
                    router.push(`/(tabs)/(provider)/services/${fixedServiceId}` as never)
                  }
                >
                  <ThemedText className="font-semibold text-xs text-primary">
                    Editar serviço ›
                  </ThemedText>
                </PressableScale>
              </View>
              <View className="mt-3 flex-row flex-wrap gap-2">
                <ThemedText className="bg-money-soft text-money rounded-lg px-2.5 py-1 font-semibold text-xs">
                  {formatCurrency(servicePrice)} / {periodWord}
                </ThemedText>
                {isSubscription ? (
                  <ThemedText className="rounded-lg bg-neutral-100 px-2.5 py-1 font-semibold text-xs text-neutral-600">
                    {serviceQuota} sessões / {periodWord}
                  </ThemedText>
                ) : null}
                {service?.durationMinutes ? (
                  <ThemedText className="rounded-lg bg-neutral-100 px-2.5 py-1 font-semibold text-xs text-neutral-600">
                    {service.durationMinutes} min
                  </ThemedText>
                ) : null}
              </View>
            </View>
          ) : (
            <ControlledDataTableSelect
              control={control}
              name="serviceId"
              label="Serviço"
              modalTitle="Buscar serviço de assinatura"
              fetchEndpoint="provider/services"
              extraParams={{ isSubscription: true }}
              initialSelectedName={serviceName}
              valueAccessor={(item: any) => item.id}
              displayAccessor={(item: any) => item.name}
              columns={[
                { header: 'Serviço', accessor: 'name', flex: 2 },
                {
                  header: 'Valor',
                  accessor: (item: any) => (
                    <ThemedText className="text-sm text-foreground">
                      {item.defaultPrice ? formatCurrency(item.defaultPrice) : '-'}
                    </ThemedText>
                  ),
                  width: 110,
                  flex: 1,
                },
              ]}
            />
          )}

          {service && !fixedServiceId ? (
            <View className="flex-row flex-wrap gap-2 px-1">
              <ThemedText className="bg-money-soft text-money rounded-lg px-2.5 py-1 font-semibold text-xs">
                {formatCurrency(servicePrice)} / {periodWord}
              </ThemedText>
              {isSubscription ? (
                <ThemedText className="rounded-lg bg-neutral-100 px-2.5 py-1 font-semibold text-xs text-neutral-600">
                  {serviceQuota} sessões / {periodWord}
                </ThemedText>
              ) : null}
            </View>
          ) : null}

          <ModeSwitch value={mode} onChange={handleModeChange} />

          <StepCard>
            <ControlledSearchableSelect<Client>
              control={control}
              name="clientId"
              label="Cliente"
              modalTitle="Buscar cliente"
              fetchEndpoint="client"
              initialSelectedName={enrollment?.client?.user?.username}
              allowCreation
              createEndpoint="client/register-by-provider"
              valueAccessor={(item) => item.id}
              displayAccessor={(item) => item.user.username}
              renderListItem={(item) => (
                <View>
                  <ThemedText className="text-base text-foreground">
                    {item.user.username}
                  </ThemedText>
                  <ThemedText className="text-sm text-card-foreground">
                    {item.user.email}
                  </ThemedText>
                </View>
              )}
            />

            {mode === 'adjust' ? (
              <>
                <ControlledInput
                  control={control}
                  name="priceOverride"
                  label={`Valor por ${periodWord}`}
                  placeholder={formatCurrency(servicePrice)}
                  maskType="currency"
                  helperText="Em branco = igual ao serviço."
                />
                {isSubscription ? (
                  <ControlledStepperRow
                    control={control}
                    name="quotaOverride"
                    title={`Sessões por ${periodWord}`}
                    note={
                      serviceQuota ? `Serviço: ${serviceQuota}. Vale só neste contrato.` : undefined
                    }
                    min={1}
                    max={31}
                  />
                ) : null}
              </>
            ) : null}

            <View className="flex-row gap-3">
              <View className="flex-1">
                <ControlledInput
                  control={control}
                  name="startDate"
                  label="Começa em"
                  placeholder="DD/MM/AAAA"
                  maskType="date"
                />
              </View>
              <View className="flex-1">
                <ControlledInput
                  control={control}
                  name="endDate"
                  label="Termina em"
                  placeholder="sem fim"
                  maskType="date"
                />
              </View>
            </View>

            {isEdit ? (
              <ControlledSelect
                control={control}
                name="status"
                label="Status"
                options={STATUS_OPTIONS}
                placeholder="Selecione"
              />
            ) : null}
          </StepCard>

          {mode === 'keep' ? (
            <ThemedText className="px-1 text-xs leading-4 text-neutral-500">
              Cobra {formatCurrency(servicePrice)}/{periodWord}
              {isSubscription ? `, ${serviceQuota} sessões/${periodWord}` : ''} — igual ao serviço.
            </ThemedText>
          ) : (
            <ThemedText className="px-1 text-xs leading-4 text-neutral-500">
              O que mudar aqui vale só para o contrato deste cliente. O serviço fica intacto.
            </ThemedText>
          )}
        </View>

        <View className="gap-3" style={{ display: step === 2 ? 'flex' : 'none' }}>
          <StepCard>
            <ControlledInput
              control={control}
              name="chargeDay"
              label="Cobrar todo dia"
              placeholder="Ex: 5"
              keyboardType="numeric"
              helperText={chargeDayNote}
            />
            <ControlledOptionRows
              control={control}
              name="reminderType"
              title="Quando avisar o cliente"
              options={REMINDER_OPTIONS}
            />
          </StepCard>

          <CollapseSection
            title="Pré-marcar horários"
            status={
              openGrade
                ? undefined
                : grade.count > 0
                  ? `${grade.count} ${grade.count > 1 ? 'horários' : 'horário'}${
                      grade.repeat ? ' · toda semana' : ''
                    }`
                  : hadGrade
                    ? 'grade atual mantida'
                    : 'opcional — nenhum'
            }
            expanded={openGrade}
            onToggle={() => setOpenGrade((v) => !v)}
          >
            <View className="gap-4">
              {hadGrade ? (
                <>
                  <ThemedText className="text-xs leading-5 text-neutral-500">
                    Grade fixa deste contrato. Para trocar os horários, recrie o contrato.
                  </ThemedText>
                  <View className="flex-row flex-wrap gap-2">
                    {(enrollment?.serviceSchedules ?? []).map((s, i) => (
                      <View key={i} className="rounded-full bg-neutral-100 px-3 py-1.5">
                        <ThemedText className="font-semibold text-xs capitalize text-neutral-700">
                          {WEEKDAYS_SHORT[s.daysOfWeek?.[0] ?? 0]} {s.startTime?.slice(0, 5)}
                        </ThemedText>
                      </View>
                    ))}
                  </View>
                </>
              ) : !isSubscription ? (
                <ThemedText className="text-xs leading-5 text-neutral-500">
                  A grade fixa vale para serviços de assinatura.
                </ThemedText>
              ) : !watchedServiceId ? (
                <ThemedText className="text-xs leading-5 text-neutral-500">
                  Escolha o serviço primeiro.
                </ThemedText>
              ) : gradeAvailability.isLoading ? (
                <ActivityIndicator color="#FD5001" className="py-3" />
              ) : (gradeAvailability.data?.days ?? []).some(
                  (d) => d.available && d.slots.length > 0,
                ) ? (
                <>
                  <ScheduleGrade
                    grade={grade}
                    days={gradeAvailability.data?.days ?? []}
                    hint={
                      'Toque nos horários livres da agenda — cada toque marca aquela data. ' +
                      'Ligue "Toda semana" para fixar o horário nas próximas semanas. ' +
                      'Datas já ocupadas são puladas.'
                    }
                  />
                </>
              ) : (
                <ThemedText className="text-xs leading-5 text-neutral-500">
                  Sem horários livres neste ciclo do contrato.
                </ThemedText>
              )}
            </View>
          </CollapseSection>

          <CollapseSection
            title="Dados de pagamento"
            status={openPayment ? undefined : 'padrão da conta'}
            expanded={openPayment}
            onToggle={() => setOpenPayment((v) => !v)}
          >
            <View className="gap-4">
              <ControlledInput
                control={control}
                name="pixCopyPaste"
                label="Pix copia e cola"
                placeholder="Cole seu código Pix"
                type="textarea"
              />
              <ControlledInput
                control={control}
                name="paymentLink"
                label="Link de pagamento"
                placeholder="https://…"
                keyboardType="url"
              />
              <View className="flex-row gap-3">
                <View className="flex-[2]">
                  <ControlledInput
                    control={control}
                    name="bankName"
                    label="Banco"
                    placeholder="Nubank"
                  />
                </View>
                <View className="flex-1">
                  <ControlledInput
                    control={control}
                    name="bankAgency"
                    label="Agência"
                    placeholder="0001"
                    keyboardType="numeric"
                  />
                </View>
                <View className="flex-1">
                  <ControlledInput
                    control={control}
                    name="bankAccount"
                    label="Conta"
                    placeholder="12345-6"
                  />
                </View>
              </View>
              <ControlledInput
                control={control}
                name="paymentInstructions"
                label="Instruções"
                placeholder="Texto que o cliente vê na cobrança"
                type="textarea"
              />
            </View>
          </CollapseSection>

          <View
            className="rounded-2xl border border-dashed border-primary/30 bg-primary/5 p-4"
            style={{ borderCurve: 'continuous' }}
          >
            <ThemedText className="font-bold text-xs uppercase tracking-wide text-neutral-400">
              Contrato
            </ThemedText>
            <ThemedText className="mt-1 text-[13px] leading-5 text-neutral-700">
              {serviceName ?? 'Serviço'} ·{' '}
              <ThemedText className="text-money font-semibold">
                {formatCurrency(
                  getValues('priceOverride')?.trim()
                    ? (parseCurrency(getValues('priceOverride')) ?? servicePrice)
                    : servicePrice,
                )}
                /{periodWord}
              </ThemedText>
              {isSubscription
                ? ` · ${getValues('quotaOverride') ?? serviceQuota} sessões/${periodWord}`
                : ''}{' '}
              · cobra dia {getValues('chargeDay')}
            </ThemedText>
          </View>
        </View>
      </PageLayout>
    </>
  );
}
